import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Search, Loader, Sparkles, Send, Mail, Calendar, RotateCcw } from 'lucide-react';
import Swal from 'sweetalert2';
import axios from 'axios';
import BASE_URL from '../../url';
import '../../Resume_Submission/styles/ScheduleInterviewModals.css';
import ShareResumeModal from '../../Resume_Submission/Components/ShareResumeModal';

const SourceCandidateModal = ({ isOpen, onClose, role, onProfileSubmitted, applications = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [matchFilter, setMatchFilter] = useState('matched'); // 'matched' | 'all'
  const [regionFilter, setRegionFilter] = useState('all'); // 'all' | 'onsite' | 'offshore'
  const [dateQuickSelect, setDateQuickSelect] = useState('all'); // 'all' | 'today' | 'yesterday' | 'this_week' | 'last_week' | 'this_month' | 'last_month' | 'custom'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submittingCandidateId, setSubmittingCandidateId] = useState(null);
  const [hiringStages, setHiringStages] = useState([]);

  // ── Bulk selection state ──────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);

  // Helper date formatting
  const toYMD = (d) => {
    if (!d) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleQuickDateChange = (val) => {
    setDateQuickSelect(val);
    const today = new Date();

    if (val === "all") {
      setStartDate("");
      setEndDate("");
      return;
    }

    let start = new Date(today);
    let end = new Date(today);

    if (val === "today") {
      start = new Date(today);
      end = new Date(today);
    } else if (val === "yesterday") {
      start.setDate(today.getDate() - 1);
      end.setDate(today.getDate() - 1);
    } else if (val === "this_week") {
      const dayOfWeek = today.getDay(); // 0 is Sunday, 1 is Monday
      const distanceToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      start.setDate(today.getDate() + distanceToMonday);
      end = new Date(today);
    } else if (val === "last_week") {
      const dayOfWeek = today.getDay();
      const distanceToLastMonday = (dayOfWeek === 0 ? -6 : 1 - dayOfWeek) - 7;
      start.setDate(today.getDate() + distanceToLastMonday);
      end = new Date(start);
      end.setDate(start.getDate() + 6);
    } else if (val === "this_month") {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today);
    } else if (val === "last_month") {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (val === "custom") {
      if (!startDate) start.setDate(today.getDate() - 30);
      if (!endDate) end = new Date(today);
    }

    setStartDate(toYMD(start));
    setEndDate(toYMD(end));
  };

  const isCandidateInRegion = (candidate, region) => {
    if (region === 'all') return true;

    const loc = (candidate.location || candidate.raw?.current_location || '').trim().toLowerCase();
    const ctry = (candidate.raw?.CountryName || candidate.raw?.CountryIso2 || candidate.raw?.country || '').trim().toLowerCase();
    const city = (candidate.raw?.CityName || candidate.raw?.city || '').trim().toLowerCase();

    // Word-boundary helper: match whole word only
    const hasWord = (str, word) => new RegExp(`(^|[^a-z])${word}([^a-z]|$)`).test(str);

    // ── US Detection ──
    const isExplicitUS = 
      ctry === 'us' || ctry === 'usa' || ctry === 'united states' || ctry === 'u.s.' ||
      loc.endsWith(', us') || loc.endsWith(', usa') || loc.endsWith(', united states') ||
      loc.includes(', us') || loc.includes('united states') || loc.endsWith(' us') || loc.endsWith(' usa');

    // ── India Detection (word-boundary safe) ──
    const indiaCountryCodes = ctry === 'india' || ctry === 'ind' || ctry === 'in';
    const indiaCities = 
      city === 'chennai' || city === 'bangalore' || city === 'bengaluru' ||
      city === 'hyderabad' || city === 'pune' || city === 'noida' ||
      city === 'gurgaon' || city === 'gurugram' || city === 'delhi' ||
      city === 'mumbai' || city === 'kolkata' || city === 'coimbatore' || city === 'kochi' ||
      city === 'ahmedabad' || city === 'jaipur' || city === 'bhopal' || city === 'lucknow';

    const indiaInLocation = 
      hasWord(loc, 'india') || hasWord(loc, 'chennai') ||
      hasWord(loc, 'bangalore') || hasWord(loc, 'bengaluru') ||
      hasWord(loc, 'hyderabad') || hasWord(loc, 'pune') ||
      hasWord(loc, 'noida') || hasWord(loc, 'gurgaon') || hasWord(loc, 'gurugram') ||
      hasWord(loc, 'delhi') || hasWord(loc, 'mumbai') ||
      hasWord(loc, 'kolkata') || hasWord(loc, 'coimbatore') || hasWord(loc, 'kochi') ||
      (hasWord(loc, 'telangana') && !isExplicitUS) ||
      (hasWord(loc, 'karnataka') && !isExplicitUS) ||
      (hasWord(loc, 'maharashtra') && !isExplicitUS) ||
      (hasWord(loc, 'tamil nadu') && !isExplicitUS) ||
      (!isExplicitUS && (indiaCountryCodes || loc.endsWith(', in') || loc.includes(', in,') || loc.includes(', ts, in') || loc.includes(', mh, in') || loc.includes(', ka, in') || loc.includes(', tn, in')));

    const isExplicitIndia = indiaCountryCodes || indiaCities || indiaInLocation;

    if (region === 'offshore') {
      // Must match India AND not be a US candidate
      if (isExplicitUS) return false;
      return isExplicitIndia;
    }

    if (region === 'onsite') {
      // Must NOT be an India-only candidate
      if (isExplicitIndia && !isExplicitUS) return false;
      return true;
    }

    return true;
  };

  const isCandidateInDateRange = (candidate, startStr, endStr) => {
    if (!startStr && !endStr) return true;
    const dateVal = candidate.raw?.created_on || candidate.raw?.CreatedAtUtc || candidate.raw?.createdAt || candidate.raw?.UploadedOn || candidate.raw?.modified_on || candidate.raw?.UpdatedAtUtc;
    if (!dateVal) return true;

    const candDate = new Date(dateVal);
    if (isNaN(candDate.getTime())) return true;

    const candYMD = toYMD(candDate);

    if (startStr && endStr) {
      return candYMD >= startStr && candYMD <= endStr;
    } else if (startStr) {
      return candYMD >= startStr;
    } else if (endStr) {
      return candYMD <= endStr;
    }
    return true;
  };

// ── In-Memory Cache for Candidates ──────────────────────────────────────────
let globalCandidateCache = null;
let globalCacheTimestamp = 0;
const CACHE_DURATION_MS = 15 * 60 * 1000; // 15 minutes

  const fetchCandidates = useCallback(async () => {
    if (!isOpen || !role) return;
    
    // Check if we have a valid cache
    const now = Date.now();
    if (globalCandidateCache && (now - globalCacheTimestamp < CACHE_DURATION_MS)) {
      setLoading(true);
      // We still need to recalculate scores because the 'role' might have changed
      setTimeout(() => {
        const scoredAll = scoreCandidates(globalCandidateCache, role);
        setCandidates(scoredAll);
        setSelectedIds([]);
        setLoading(false);
      }, 50); // slight delay to allow UI to render modal first
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/resumes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const rawCandidates = Array.isArray(res.data) 
        ? res.data 
        : (res.data.data || res.data.candidates || []);

      // Save to global cache
      globalCandidateCache = rawCandidates;
      globalCacheTimestamp = Date.now();

      const scoredAll = scoreCandidates(rawCandidates, role);
      setCandidates(scoredAll);
      setSelectedIds([]); // reset selection on fresh load
    } catch (error) {
      console.error('Error fetching candidates:', error);
      Swal.fire('Error', 'Failed to fetch candidates matching this role.', 'error');
    } finally {
      setLoading(false);
    }
  }, [isOpen, role]);

  const scoreCandidates = (rawCandidates, currentRole) => {
    const jobTitle = (currentRole.role || '').toLowerCase();
    const jobDesc = (currentRole.jobDescription || '').replace(/<[^>]*>?/gm, '').toLowerCase();
    // Split job title into individual keywords for broader matching
    const jobKeywords = jobTitle.split(/[\s,\/\-]+/).filter(w => w.length > 2);

    const scoredCandidates = rawCandidates.map(candidate => {
      const c = {
        id: candidate.candidate_id || candidate.CandidateId,
        firstName: candidate.first_name || candidate.FirstName || '',
        lastName: candidate.last_name || candidate.LastName || '',
        email: candidate.email || candidate.Contact?.Email || '',
        phone: candidate.phone || candidate.Contact?.Phone || candidate.Mobile || '',
        jobTitle: (candidate.job_title || candidate.JobTitle || '').toLowerCase(),
        skills: Array.isArray(candidate.skills) 
          ? candidate.skills.map(s => (typeof s === 'string' ? s : s.skill_name || s.SkillName || '').toLowerCase())
          : (candidate.skills || '').toString().toLowerCase().split(',').map(s => s.trim()).filter(Boolean),
        experience: candidate.years_exp || candidate.YearsOfExperience || 0,
        location: candidate.current_location || candidate.CurrentLocation || 'N/A',
        isBench: candidate.is_bench || candidate.IsBench || false,
        raw: candidate
      };

      let score = 0;
      // Exact job title match (high score)
      if (jobTitle && c.jobTitle && (c.jobTitle.includes(jobTitle) || jobTitle.includes(c.jobTitle))) score += 15;
      // Keyword-level match against candidate job title
      jobKeywords.forEach(keyword => {
        if (c.jobTitle.includes(keyword)) score += 3;
      });
      // Skill matches
      c.skills.forEach(skill => {
        if (jobTitle.includes(skill) || jobKeywords.some(k => skill.includes(k) || k.includes(skill))) score += 5;
        if (jobDesc && jobDesc.includes(skill)) score += 2;
      });

      return { ...c, score };
    });

    // Return all sorted by relevance score descending
    return scoredCandidates.sort((a, b) => b.score - a.score);
  };


  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  useEffect(() => {
    const fetchStages = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${BASE_URL}/api/recruitment/applications/hiring-steps`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const progressive = (res.data || []).filter(s => {
          const code = (s.stageCode || '').toUpperCase();
          const name = (s.stepName || '').toLowerCase().trim();
          return !['REJECTED', 'WITHDRAWN', 'REJECTED_INTERNAL', 'WITHDRAWN_INTERNAL', 'HOLD', 'ON_HOLD', 'HOLD_INTERNAL'].includes(code) &&
                 !name.includes('rejected') && !name.includes('withdrawn') && !name.includes('hold');
        });
        setHiringStages(progressive);
      } catch (err) {
        console.error('Failed to fetch hiring stages:', err);
      }
    };
    fetchStages();
  }, []);

  // Reset selection when modal closes
  useEffect(() => {
    if (!isOpen) setSelectedIds([]);
  }, [isOpen]);

  const handleSubmitProfile = async (candidate) => {
    const candidateName = `${candidate.firstName} ${candidate.lastName}`.trim();
    const confirmText = `You are submitting <b>${candidateName}</b> for the role <b>"${role.role}"</b>`;

    const defaultIdx = hiringStages.findIndex((s, idx) =>
      s.stageCode === 'RESUME_REVIEW' || s.stepName?.toLowerCase().includes('resume review') || idx === 1
    );
    const initialSelectedIdx = defaultIdx >= 0 ? defaultIdx : 0;
    const initialStage = hiringStages[initialSelectedIdx] || {};

    let stagesListHtml = '';
    hiringStages.forEach((stage, idx) => {
      const isDefault = idx === initialSelectedIdx;
      stagesListHtml += `
        <div 
          class="swal-stage-row" 
          id="swal-src-stage-row-${idx}"
          data-idx="${idx}" 
          style="display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; margin-bottom: 5px; border-radius: 7px; border: 1.5px solid ${isDefault ? '#3b5bfd' : '#e2e8f0'}; background: ${isDefault ? '#f0fdfa' : '#ffffff'}; cursor: pointer; transition: all 0.15s ease;"
        >
          <div class="swal-src-stage-select-target" data-idx="${idx}" style="display: flex; align-items: center; gap: 9px; flex: 1; text-align: left;">
            <input 
              type="radio" 
              name="swal_src_stage_radio" 
              id="swal-src-stage-radio-${idx}" 
              value="${idx}" 
              ${isDefault ? 'checked' : ''} 
              style="accent-color: #3b5bfd; width: 16px; height: 16px; cursor: pointer;"
            />
            <div>
              <div style="font-size: 13px; font-weight: 700; color: #1e293b;">
                ${idx + 1}. ${stage.stepName}
              </div>
              ${stage.phaseCode ? `<div style="font-size: 10px; font-weight: 600; color: #64748b; text-transform: uppercase;">${stage.phaseCode} PHASE</div>` : ''}
            </div>
          </div>
          <div style="display: flex; align-items: center; gap: 5px; flex-shrink: 0;">
            <button 
              type="button" 
              class="swal-stage-action-btn swal-src-reject-btn" 
              id="swal-src-reject-btn-${idx}"
              data-idx="${idx}" 
              title="Reject candidate at ${stage.stepName}" 
              style="display: inline-flex; align-items: center; gap: 2px; padding: 4px 7px; font-size: 11px; font-weight: 700; border-radius: 5px; border: 1px solid #fca5a5; background: #ffffff; color: #dc2626; cursor: pointer; transition: all 0.15s ease;"
            >
              🔴 Reject
            </button>
            <button 
              type="button" 
              class="swal-stage-action-btn swal-src-hold-btn" 
              id="swal-src-hold-btn-${idx}"
              data-idx="${idx}" 
              title="Hold candidate at ${stage.stepName}" 
              style="display: inline-flex; align-items: center; gap: 2px; padding: 4px 7px; font-size: 11px; font-weight: 700; border-radius: 5px; border: 1px solid #c4b5fd; background: #ffffff; color: #7c3aed; cursor: pointer; transition: all 0.15s ease;"
            >
              ⏸ Hold
            </button>
            <button 
              type="button" 
              class="swal-stage-action-btn swal-src-withdraw-btn" 
              id="swal-src-withdraw-btn-${idx}"
              data-idx="${idx}" 
              title="Withdraw candidate at ${stage.stepName}" 
              style="display: inline-flex; align-items: center; gap: 2px; padding: 4px 7px; font-size: 11px; font-weight: 700; border-radius: 5px; border: 1px solid #fcd34d; background: #ffffff; color: #d97706; cursor: pointer; transition: all 0.15s ease;"
            >
              🟠 Withdraw
            </button>
          </div>
        </div>
      `;
    });

    let selectedStepIndex = initialSelectedIdx;
    let selectedOutcomeType = 'Active'; // 'Active' | 'Rejected' | 'Withdrawn' | 'Hold'

    const confirm = await Swal.fire({
      title: 'Submit Profile?',
      html: `
        <div style="font-size: 13.5px; color: #334155; line-height: 1.5; margin-bottom: 12px; text-align: left;">${confirmText}</div>

        <!-- Action Status Feedback Banner -->
        <div 
          id="source-action-banner" 
          style="background: #f0fdfa; border: 1px solid #99f6e4; border-radius: 8px; padding: 10px 14px; margin-bottom: 12px; display: flex; align-items: center; justify-content: space-between; gap: 10px; text-align: left;"
        >
          <div id="source-action-text" style="font-size: 13px; font-weight: 700; color: #1d4ed8;">
            🟢 Action: Move to <strong>${initialStage.stepName || 'Resume Review'}</strong> (Active)
          </div>
          <span 
            id="source-action-badge" 
            style="font-size: 11px; font-weight: 800; padding: 3px 8px; border-radius: 12px; background: #ccfbf1; color: #1d4ed8; text-transform: uppercase; letter-spacing: 0.5px; flex-shrink: 0;"
          >
            ACTIVE
          </span>
        </div>

        <div style="text-align: left; margin-bottom: 6px;">
          <label style="font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px;">
            Pipeline Stage & Action (Click step to advance, or click 🔴/⏸/🟠 to reject/hold/withdraw) <span style="color: #ef4444;">*</span>
          </label>
        </div>

        <!-- Scrollable Stage List with per-step icons -->
        <div 
          id="source-stage-list-container" 
          style="max-height: 250px; overflow-y: auto; border: 1px solid #e2e8f0; border-radius: 8px; background: #f8fafc; padding: 6px; margin-bottom: 12px;"
        >
          ${stagesListHtml}
        </div>

        <div style="text-align: left; margin-top: 10px;">
          <label id="source-notes-label" style="font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 4px;">
            Stage Notes / Remarks (Optional)
          </label>
          <textarea 
            id="source-candidate-notes" 
            placeholder="Add candidate evaluation, remarks, or notes for this stage..." 
            rows="3" 
            style="width: 100%; padding: 8px 10px; border-radius: 6px; border: 1px solid #cbd5e1; font-size: 13px; font-family: inherit; color: #0f172a; box-sizing: border-box; outline: none; resize: vertical; line-height: 1.45;"
          ></textarea>
        </div>

        <div id="source-entity-container" style="display: none; margin-top: 12px; background: #f8fafc; padding: 12px; border-radius: 8px; border: 1px solid #e2e8f0; text-align: left;">
          <label style="font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; display: block;">
            Select Onboarding Entity / Company *
          </label>
          <select id="source-onboarding-entity" style="width: 100%; padding: 8px 10px; border-radius: 6px; border: 1px solid #cbd5e1; font-size: 13.5px; font-weight: 500; color: #0f172a; background: #ffffff; outline: none;">
            <option value="4" selected>Prophecy Consulting INC (US Entity)</option>
            <option value="5">Prophecy Offshore (India Entity)</option>
            <option value="6">Cognifyar Technologies</option>
          </select>
        </div>
      `,
      width: '560px',
      showCancelButton: true,
      confirmButtonText: 'Yes, Submit',
      confirmButtonColor: '#3b5bfd',
      didOpen: () => {
        const bannerEl = document.getElementById('source-action-banner');
        const bannerTextEl = document.getElementById('source-action-text');
        const bannerBadgeEl = document.getElementById('source-action-badge');
        const notesLabelEl = document.getElementById('source-notes-label');
        const notesEl = document.getElementById('source-candidate-notes');
        const entityContainer = document.getElementById('source-entity-container');
        const confirmBtn = Swal.getConfirmButton();

        const updateSourceState = (idx, outcomeType) => {
          selectedStepIndex = idx;
          selectedOutcomeType = outcomeType;
          const currentStage = hiringStages[idx] || {};
          const stageName = currentStage.stepName || `Stage ${idx + 1}`;

          // 1. Update rows and buttons
          hiringStages.forEach((s, sIdx) => {
            const row = document.getElementById(`swal-src-stage-row-${sIdx}`);
            const radio = document.getElementById(`swal-src-stage-radio-${sIdx}`);
            const rejBtn = document.getElementById(`swal-src-reject-btn-${sIdx}`);
            const holdBtn = document.getElementById(`swal-src-hold-btn-${sIdx}`);
            const wdrBtn = document.getElementById(`swal-src-withdraw-btn-${sIdx}`);

            if (sIdx === idx) {
              if (radio) radio.checked = true;
              if (outcomeType === 'Rejected') {
                if (row) { row.style.background = '#fef2f2'; row.style.border = '1.5px solid #ef4444'; }
                if (rejBtn) { rejBtn.style.background = '#dc2626'; rejBtn.style.color = '#ffffff'; rejBtn.style.border = '1px solid #dc2626'; }
                if (holdBtn) { holdBtn.style.background = '#ffffff'; holdBtn.style.color = '#7c3aed'; holdBtn.style.border = '1px solid #c4b5fd'; }
                if (wdrBtn) { wdrBtn.style.background = '#ffffff'; wdrBtn.style.color = '#d97706'; wdrBtn.style.border = '1px solid #fcd34d'; }
              } else if (outcomeType === 'Hold') {
                if (row) { row.style.background = '#f5f3ff'; row.style.border = '1.5px solid #7c3aed'; }
                if (rejBtn) { rejBtn.style.background = '#ffffff'; rejBtn.style.color = '#dc2626'; rejBtn.style.border = '1px solid #fca5a5'; }
                if (holdBtn) { holdBtn.style.background = '#7c3aed'; holdBtn.style.color = '#ffffff'; holdBtn.style.border = '1px solid #7c3aed'; }
                if (wdrBtn) { wdrBtn.style.background = '#ffffff'; wdrBtn.style.color = '#d97706'; wdrBtn.style.border = '1px solid #fcd34d'; }
              } else if (outcomeType === 'Withdrawn') {
                if (row) { row.style.background = '#fffbeb'; row.style.border = '1.5px solid #f59e0b'; }
                if (rejBtn) { rejBtn.style.background = '#ffffff'; rejBtn.style.color = '#dc2626'; rejBtn.style.border = '1px solid #fca5a5'; }
                if (holdBtn) { holdBtn.style.background = '#ffffff'; holdBtn.style.color = '#7c3aed'; holdBtn.style.border = '1px solid #c4b5fd'; }
                if (wdrBtn) { wdrBtn.style.background = '#d97706'; wdrBtn.style.color = '#ffffff'; wdrBtn.style.border = '1px solid #d97706'; }
              } else {
                if (row) { row.style.background = '#f0fdfa'; row.style.border = '1.5px solid #3b5bfd'; }
                if (rejBtn) { rejBtn.style.background = '#ffffff'; rejBtn.style.color = '#dc2626'; rejBtn.style.border = '1px solid #fca5a5'; }
                if (holdBtn) { holdBtn.style.background = '#ffffff'; holdBtn.style.color = '#7c3aed'; holdBtn.style.border = '1px solid #c4b5fd'; }
                if (wdrBtn) { wdrBtn.style.background = '#ffffff'; wdrBtn.style.color = '#d97706'; wdrBtn.style.border = '1px solid #fcd34d'; }
              }
            } else {
              if (radio) radio.checked = false;
              if (row) { row.style.background = '#ffffff'; row.style.border = '1px solid #e2e8f0'; }
              if (rejBtn) { rejBtn.style.background = '#ffffff'; rejBtn.style.color = '#dc2626'; rejBtn.style.border = '1px solid #fca5a5'; }
              if (holdBtn) { holdBtn.style.background = '#ffffff'; holdBtn.style.color = '#7c3aed'; holdBtn.style.border = '1px solid #c4b5fd'; }
              if (wdrBtn) { wdrBtn.style.background = '#ffffff'; wdrBtn.style.color = '#d97706'; wdrBtn.style.border = '1px solid #fcd34d'; }
            }
          });

          // 2. Update Banner and Confirm Button
          if (outcomeType === 'Rejected') {
            if (bannerEl) { bannerEl.style.background = '#fef2f2'; bannerEl.style.border = '1px solid #fca5a5'; }
            if (bannerTextEl) { bannerTextEl.innerHTML = `🔴 Action: REJECT candidate at <strong>${stageName}</strong>`; bannerTextEl.style.color = '#b91c1c'; }
            if (bannerBadgeEl) { bannerBadgeEl.innerText = 'REJECTED'; bannerBadgeEl.style.background = '#fee2e2'; bannerBadgeEl.style.color = '#dc2626'; }
            if (notesLabelEl) { notesLabelEl.innerHTML = `Rejection Reason (Required) <span style="color: #ef4444;">*</span>`; }
            if (notesEl) { notesEl.placeholder = `Please provide the reason for rejection at ${stageName}...`; notesEl.style.borderColor = '#fca5a5'; notesEl.focus(); }
            if (confirmBtn) { confirmBtn.innerText = 'Confirm Rejection'; confirmBtn.style.backgroundColor = '#ef4444'; }
            if (entityContainer) entityContainer.style.display = 'none';
          } else if (outcomeType === 'Hold') {
            if (bannerEl) { bannerEl.style.background = '#f5f3ff'; bannerEl.style.border = '1px solid #c4b5fd'; }
            if (bannerTextEl) { bannerTextEl.innerHTML = `⏸ Action: Place candidate on HOLD at <strong>${stageName}</strong>`; bannerTextEl.style.color = '#6d28d9'; }
            if (bannerBadgeEl) { bannerBadgeEl.innerText = 'ON HOLD'; bannerBadgeEl.style.background = '#ede9fe'; bannerBadgeEl.style.color = '#7c3aed'; }
            if (notesLabelEl) { notesLabelEl.innerHTML = `Hold Reason (Required) <span style="color: #7c3aed;">*</span>`; }
            if (notesEl) { notesEl.placeholder = `Please provide the reason for placing candidate on hold at ${stageName}...`; notesEl.style.borderColor = '#c4b5fd'; notesEl.focus(); }
            if (confirmBtn) { confirmBtn.innerText = 'Confirm Hold'; confirmBtn.style.backgroundColor = '#7c3aed'; }
            if (entityContainer) entityContainer.style.display = 'none';
          } else if (outcomeType === 'Withdrawn') {
            if (bannerEl) { bannerEl.style.background = '#fffbeb'; bannerEl.style.border = '1px solid #fcd34d'; }
            if (bannerTextEl) { bannerTextEl.innerHTML = `🟠 Action: WITHDRAW candidate at <strong>${stageName}</strong>`; bannerTextEl.style.color = '#b45309'; }
            if (bannerBadgeEl) { bannerBadgeEl.innerText = 'WITHDRAWN'; bannerBadgeEl.style.background = '#fef3c7'; bannerBadgeEl.style.color = '#d97706'; }
            if (notesLabelEl) { notesLabelEl.innerHTML = `Withdrawal Reason (Required) <span style="color: #d97706;">*</span>`; }
            if (notesEl) { notesEl.placeholder = `Please provide the reason for withdrawal at ${stageName}...`; notesEl.style.borderColor = '#fcd34d'; notesEl.focus(); }
            if (confirmBtn) { confirmBtn.innerText = 'Confirm Withdrawal'; confirmBtn.style.backgroundColor = '#d97706'; }
            if (entityContainer) entityContainer.style.display = 'none';
          } else {
            if (bannerEl) { bannerEl.style.background = '#f0fdfa'; bannerEl.style.border = '1px solid #99f6e4'; }
            if (bannerTextEl) { bannerTextEl.innerHTML = `🟢 Action: Move to <strong>${stageName}</strong> (Active)`; bannerTextEl.style.color = '#1d4ed8'; }
            if (bannerBadgeEl) { bannerBadgeEl.innerText = 'ACTIVE'; bannerBadgeEl.style.background = '#ccfbf1'; bannerBadgeEl.style.color = '#1d4ed8'; }
            if (notesLabelEl) { notesLabelEl.innerHTML = `Stage Notes / Remarks (Optional)`; }
            if (notesEl) { notesEl.placeholder = `Add remarks or notes for this stage...`; notesEl.style.borderColor = '#cbd5e1'; }
            if (confirmBtn) { confirmBtn.innerText = 'Yes, Submit'; confirmBtn.style.backgroundColor = '#3b5bfd'; }
            
            const isOnboarding = (currentStage.stageCode || '').toUpperCase() === 'ONBOARDING_INITIATED' || 
                                 (currentStage.stepName || '').toLowerCase().includes('onboarding initiate');
            if (entityContainer) entityContainer.style.display = isOnboarding ? 'block' : 'none';
          }
        };

        // Attach click handlers
        hiringStages.forEach((s, idx) => {
          const row = document.getElementById(`swal-src-stage-row-${idx}`);
          const selectTarget = row ? row.querySelector('.swal-src-stage-select-target') : null;
          const radio = document.getElementById(`swal-src-stage-radio-${idx}`);
          const rejBtn = document.getElementById(`swal-src-reject-btn-${idx}`);
          const holdBtn = document.getElementById(`swal-src-hold-btn-${idx}`);
          const wdrBtn = document.getElementById(`swal-src-withdraw-btn-${idx}`);

          const selectActive = () => updateSourceState(idx, 'Active');
          if (selectTarget) selectTarget.addEventListener('click', selectActive);
          if (radio) radio.addEventListener('click', selectActive);

          if (rejBtn) {
            rejBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              updateSourceState(idx, 'Rejected');
            });
          }

          if (holdBtn) {
            holdBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              updateSourceState(idx, 'Hold');
            });
          }

          if (wdrBtn) {
            wdrBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              updateSourceState(idx, 'Withdrawn');
            });
          }
        });

        const initialStageObj = hiringStages[selectedStepIndex] || {};
        const isInitialOnboarding = (initialStageObj.stageCode || '').toUpperCase() === 'ONBOARDING_INITIATED' || 
                                    (initialStageObj.stepName || '').toLowerCase().includes('onboarding initiate');
        if (entityContainer) entityContainer.style.display = isInitialOnboarding ? 'block' : 'none';
      },
      preConfirm: () => {
        const notesEl = document.getElementById('source-candidate-notes');
        const entityEl = document.getElementById('source-onboarding-entity');
        const stageObj = hiringStages[selectedStepIndex];
        if (!stageObj) {
          Swal.showValidationMessage('Please select a pipeline stage');
          return false;
        }

        const notesVal = notesEl ? notesEl.value.trim() : '';
        if ((selectedOutcomeType === 'Rejected' || selectedOutcomeType === 'Withdrawn' || selectedOutcomeType === 'Hold') && !notesVal) {
          Swal.showValidationMessage(`Please enter a reason for ${selectedOutcomeType.toLowerCase()} at ${stageObj.stepName}`);
          return false;
        }

        const isOnboarding = (stageObj.stageCode || '').toUpperCase() === 'ONBOARDING_INITIATED' || 
                             (stageObj.stepName || '').toLowerCase().includes('onboarding initiate');
        return {
          stageCode: stageObj.stageCode,
          phaseCode: selectedOutcomeType !== 'Active' ? 'OUTCOME' : stageObj.phaseCode,
          stepOrder: stageObj.stepOrder,
          stepIndex: selectedStepIndex,
          stepName: stageObj.stepName,
          outcomeType: selectedOutcomeType,
          status: selectedOutcomeType !== 'Active' ? selectedOutcomeType : 'Active',
          rejectedAtStage: selectedOutcomeType !== 'Active' ? stageObj.stepName : null,
          fromStageCode: stageObj.stageCode,
          notes: notesVal,
          entityId: (isOnboarding && entityEl && selectedOutcomeType === 'Active') ? parseInt(entityEl.value) : undefined
        };
      }
    });

    if (!confirm.isConfirmed) return;
    const selectedStage = confirm.value;

    const candidateKey = candidate.id || candidate.email || candidateName;
    setSubmittingCandidateId(candidateKey);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      if (selectedStage) {
          const isOutcome = selectedStage.outcomeType === 'Rejected' || selectedStage.outcomeType === 'Withdrawn' || selectedStage.outcomeType === 'Hold';
          if (isOutcome) {
              formData.append('status', selectedStage.outcomeType);
              formData.append('rejectedAtStage', selectedStage.stepName || '');
              formData.append('fromStageCode', selectedStage.stageCode || 'NEW');
              const outcomeStageCode = selectedStage.outcomeType === 'Rejected' ? 'REJECTED' : (selectedStage.outcomeType === 'Hold' ? 'HOLD' : 'WITHDRAWN');
              formData.append('currentStageCode', outcomeStageCode);
              formData.append('currentPhaseCode', 'OUTCOME');
              formData.append('currentStep', selectedStage.stepIndex !== undefined ? selectedStage.stepIndex : (selectedStage.stepOrder || 0));
          } else {
              formData.append('currentStageCode', selectedStage.stageCode || 'NEW');
              formData.append('currentPhaseCode', selectedStage.phaseCode || 'INTERNAL');
              formData.append('currentStep', selectedStage.stepIndex !== undefined ? selectedStage.stepIndex : (selectedStage.stepOrder || 0));
          }
          if (selectedStage.notes) {
              formData.append('notes', selectedStage.notes);
          }
          if (selectedStage.entityId) {
              formData.append('entityId', selectedStage.entityId);
          }
      }
      formData.append('roleId', role.id);
      formData.append('candidateFirstName', candidate.firstName);
      formData.append('candidateLastName', candidate.lastName);
      formData.append('name', `${candidate.firstName} ${candidate.lastName}`.trim());
      formData.append('email', candidate.email);
      formData.append('phone', candidate.phone);
      formData.append('experience', candidate.experience);
      formData.append('location', candidate.location);
      formData.append('currentLocation', candidate.location);
      formData.append('skills', candidate.skills.join(', '));
      
      const raw = candidate.raw;
      formData.append('workAuthorization', raw.work_authorization || raw.WorkAuthorization || '');
      formData.append('expectedSalary', raw.expected_rate_from || raw.ExpectedRateFrom || '');
      formData.append('rate', raw.current_rate || raw.CurrentRate || '');
      formData.append('currentEmployer', raw.current_employer || raw.CurrentEmployer || '');
      formData.append('noticePeriod', raw.notice_period || raw.NoticePeriod || '');

      if (candidate.id) {
        try {
          let documentId = raw.document?.DocumentId || raw.document_id;
          if (!documentId && raw.documents) {
            const primaryDoc = raw.documents.find(d => d.is_primary || d.IsPrimary) || raw.documents[0];
            documentId = primaryDoc?.document_id || primaryDoc?.DocumentId;
          }
          if (documentId) {
            const downloadUrl = `${BASE_URL}/api/resumes/${documentId}/download`;
            const resumeRes = await axios.get(downloadUrl, {
              headers: { Authorization: `Bearer ${token}` },
              responseType: 'blob'
            });
            if (resumeRes.data && resumeRes.data.size > 0) {
              const contentDisp = resumeRes.headers['content-disposition'];
              let fileName = 'resume.pdf';
              if (contentDisp && contentDisp.includes('filename=')) {
                fileName = contentDisp.split('filename=')[1].replace(/[";]/g, '');
              }
              const file = new File([resumeRes.data], fileName, { type: resumeRes.data.type || 'application/pdf' });
              formData.append('resume', file);
            }
          }
        } catch (err) {
          console.warn('⚠️ Could not attach resume:', err.message);
        }
      }

      const response = await axios.post(`${BASE_URL}/api/recruitment/applications`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        await Swal.fire('Submitted!', 'Candidate has been submitted successfully.', 'success');
        if (onProfileSubmitted) onProfileSubmitted();
        onClose();
      } else {
        throw new Error(response.data.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Error submitting profile:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to submit profile.', 'error');
    } finally {
      setSubmittingCandidateId(null);
    }
  };

  // ── Multi-criteria filteredCandidates ──────────────────────────────────────
  const filteredCandidates = useMemo(() => {
    return candidates.filter(c => {
      const searchLower = searchTerm.trim().toLowerCase();
      
      // 1. Text Search across Name, Email, Job Title, Phone, Location, Experience, Skills, Score
      const matchesSearch = !searchLower ? true : (
        `${c.firstName} ${c.lastName}`.toLowerCase().includes(searchLower) ||
        (c.email || '').toLowerCase().includes(searchLower) ||
        (c.jobTitle || '').toLowerCase().includes(searchLower) ||
        (c.phone || '').toLowerCase().includes(searchLower) ||
        (c.location || '').toLowerCase().includes(searchLower) ||
        (c.experience || 0).toString().includes(searchLower) ||
        (c.score || 0).toString().includes(searchLower) ||
        (c.isBench ? 'bench yes' : 'no').includes(searchLower) ||
        c.skills.some(skill => (skill || '').toLowerCase().includes(searchLower))
      );

      // 2. Match Mode Filter: 'matched' (Score > 0) vs 'all' (All Candidates)
      const matchesMatchFilter = matchFilter === 'all' || c.score > 0;

      // 3. Region Filter: All / Onsite / Offshore
      const matchesRegion = isCandidateInRegion(c, regionFilter);

      // 4. Date Range Filter
      const matchesDate = isCandidateInDateRange(c, startDate, endDate);

      return matchesSearch && matchesMatchFilter && matchesRegion && matchesDate;
    });
  }, [candidates, searchTerm, matchFilter, regionFilter, startDate, endDate]);

  // ── Checkbox helpers ──────────────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredCandidates.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredCandidates.map(c => c.id));
    }
  };

  const isAllSelected = filteredCandidates.length > 0 && selectedIds.length === filteredCandidates.length;
  const isIndeterminate = selectedIds.length > 0 && selectedIds.length < filteredCandidates.length;

  // Build candidate objects for ShareResumeModal (same shape the modal expects)
  const selectedCandidatesForShare = candidates
    .filter(c => selectedIds.includes(c.id))
    .map(c => ({
      CandidateId: c.id,
      FirstName: c.firstName,
      LastName: c.lastName,
      email: c.email, // Passing email down for auto-population
      Document: c.raw?.document ? {
        DocumentId: c.raw.document.DocumentId,
        FileNameOriginal: c.raw.document.FileNameOriginal,
        FileSizeBytes: c.raw.document.FileSizeBytes,
        IsPrimary: true,
      } : null,
      Documents: c.raw?.documents?.map(d => ({
        DocumentId: d.DocumentId || d.document_id,
        FileNameOriginal: d.FileNameOriginal || d.file_name_original,
        FileSizeBytes: d.FileSizeBytes || d.file_size_bytes,
        IsPrimary: d.IsPrimary || d.is_primary,
      })) || [],
    }));

  if (!isOpen) return null;

  return (
    <>
      <div className="si-modal-overlay" style={{ zIndex: 1000 }}>
        <div className="jobs-modal-container" style={{ maxWidth: '1600px', width: '98%' }}>
          
          <div className="si-modal-header" style={{ background: '#3b5bfd', borderBottom: 'none', padding: '18px 24px' }}>
            <h2 className="si-modal-title" style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '18px' }}>
              <Sparkles size={20} fill="#fff" style={{ opacity: 0.9 }} /> Source Candidates for "{role.role}"
            </h2>
            <button 
              className="si-modal-close" 
              onClick={onClose} 
              style={{ 
                background: 'rgba(255,255,255,0.15)', 
                color: 'white',
                border: 'none',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
            >
              <X size={18} />
            </button>
          </div>

          <div className="jobs-modal-content" style={{ padding: '20px 24px' }}>
            {/* ── Filter Toolbar ── */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: '#f8fafc',
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              marginBottom: '16px'
            }}>
              {/* Search Box */}
              <div style={{ position: 'relative', flex: '1 1 200px', minWidth: '180px', maxWidth: '320px' }}>
                <Search style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} size={15} />
                <input 
                  type="text" 
                  placeholder="Search candidate, skill, title, location..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '7px 10px 7px 32px',
                    fontSize: '13px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    background: '#fff',
                    outline: 'none'
                  }}
                />
              </div>

              {/* Match Mode Dropdown */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Sparkles size={14} style={{ color: '#3b5bfd' }} />
                <select
                  value={matchFilter}
                  onChange={(e) => setMatchFilter(e.target.value)}
                  style={{
                    padding: '6px 10px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#1e293b',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    cursor: 'pointer',
                    outline: 'none'
                  }}
                >
                  <option value="matched">✨ Matched Only</option>
                  <option value="all">📋 All Candidates</option>
                </select>
              </div>

              {/* Region Filter Buttons */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#e2e8f0', padding: '3px', borderRadius: '8px' }}>
                <button
                  type="button"
                  onClick={() => setRegionFilter('all')}
                  style={{
                    padding: '5px 14px',
                    borderRadius: '6px',
                    border: 'none',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: regionFilter === 'all' ? '#3b5bfd' : 'transparent',
                    color: regionFilter === 'all' ? '#ffffff' : '#475569',
                    boxShadow: regionFilter === 'all' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.15s ease'
                  }}
                >
                  All Regions
                </button>
                <button
                  type="button"
                  onClick={() => setRegionFilter('onsite')}
                  style={{
                    padding: '5px 14px',
                    borderRadius: '6px',
                    border: 'none',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: regionFilter === 'onsite' ? '#3b5bfd' : 'transparent',
                    color: regionFilter === 'onsite' ? '#ffffff' : '#475569',
                    boxShadow: regionFilter === 'onsite' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.15s ease'
                  }}
                >
                  Onsite
                </button>
                <button
                  type="button"
                  onClick={() => setRegionFilter('offshore')}
                  style={{
                    padding: '5px 14px',
                    borderRadius: '6px',
                    border: 'none',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: regionFilter === 'offshore' ? '#3b5bfd' : 'transparent',
                    color: regionFilter === 'offshore' ? '#ffffff' : '#475569',
                    boxShadow: regionFilter === 'offshore' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.15s ease'
                  }}
                >
                  Offshore
                </button>
              </div>

              {/* Date Quick Select & Range */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Calendar size={14} style={{ color: '#64748b' }} />
                  <select
                    value={dateQuickSelect}
                    onChange={(e) => handleQuickDateChange(e.target.value)}
                    style={{
                      padding: '6px 10px',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#334155',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      background: '#ffffff',
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                  >
                    <option value="all">All Dates</option>
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="this_week">This Week</option>
                    <option value="last_week">Last Week</option>
                    <option value="this_month">This Month</option>
                    <option value="last_month">Last Month</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>

                {(dateQuickSelect === 'custom' || startDate || endDate) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <input 
                      type="date"
                      value={startDate}
                      onChange={(e) => { setStartDate(e.target.value); setDateQuickSelect('custom'); }}
                      style={{ padding: '4px 6px', fontSize: '11px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                    />
                    <span style={{ fontSize: '11px', color: '#64748b' }}>to</span>
                    <input 
                      type="date"
                      value={endDate}
                      onChange={(e) => { setEndDate(e.target.value); setDateQuickSelect('custom'); }}
                      style={{ padding: '4px 6px', fontSize: '11px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                    />
                  </div>
                )}

                {/* Reset Filters Button */}
                {(searchTerm || matchFilter !== 'matched' || regionFilter !== 'all' || dateQuickSelect !== 'all') && (
                  <button
                    type="button"
                    title="Reset All Filters"
                    onClick={() => {
                      setSearchTerm('');
                      setMatchFilter('matched');
                      setRegionFilter('all');
                      setDateQuickSelect('all');
                      setStartDate('');
                      setEndDate('');
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '5px 10px',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#64748b',
                      background: '#ffffff',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    <RotateCcw size={12} /> Reset
                  </button>
                )}

                {/* ── Bulk Email Button in Toolbar ─────────────────────────────── */}
                {selectedIds.length > 0 && (
                  <button
                    onClick={() => setShowShareModal(true)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 14px',
                      background: '#3b5bfd',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontWeight: 600,
                      fontSize: '12px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 6px rgba(26,111,102,0.3)',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#124d47'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#3b5bfd'; }}
                  >
                    <Mail size={13} />
                    Bulk Email ({selectedIds.length})
                  </button>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                Showing <strong>{filteredCandidates.length}</strong> of <strong>{candidates.length}</strong> {matchFilter === 'matched' ? 'matched ' : ''}candidates for <strong>"{role.role}"</strong>.
                {selectedIds.length > 0 && (
                  <span style={{ marginLeft: '10px', color: '#3b5bfd', fontWeight: 600 }}>
                    ({selectedIds.length} selected)
                  </span>
                )}
              </p>
            </div>

            <div style={{ overflowX: 'auto', maxHeight: '55vh', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <table className="jobs-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
                  <tr>
                    {/* ── Select-All checkbox column ── */}
                    <th style={{ width: '38px', padding: '10px 8px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        ref={el => { if (el) el.indeterminate = isIndeterminate; }}
                        onChange={toggleSelectAll}
                        style={{ accentColor: '#3b5bfd', width: 16, height: 16, cursor: 'pointer' }}
                        title={isAllSelected ? 'Deselect all' : 'Select all'}
                      />
                    </th>
                    <th style={{ textAlign: 'left', padding: '10px 10px', fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.5px', width: '14.5%' }}>CANDIDATE NAME</th>
                    <th style={{ textAlign: 'center', padding: '10px 6px', fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.5px', width: '5.5%' }}>SCORE</th>
                    <th style={{ textAlign: 'left', padding: '10px 10px', fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.5px', width: '13.5%' }}>JOB TITLE</th>
                    <th style={{ textAlign: 'left', padding: '10px 10px', fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.5px', width: '14.5%' }}>EMAIL</th>
                    <th style={{ textAlign: 'left', padding: '10px 10px', fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.5px', width: '10%' }}>PHONE</th>
                    <th style={{ textAlign: 'left', padding: '10px 10px', fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.5px', width: '10.5%' }}>LOCATION</th>
                    <th style={{ textAlign: 'left', padding: '10px 8px', fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.5px', width: '5%' }}>EXP.</th>
                    <th style={{ textAlign: 'left', padding: '10px 10px', fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.5px', width: '11%' }}>SKILLS</th>
                    <th style={{ textAlign: 'center', padding: '10px 6px', fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.5px', width: '6.5%' }}>BENCH CAN</th>
                    <th style={{ textAlign: 'center', padding: '10px 8px', fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.5px', width: '9%' }}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="11" style={{ textAlign: 'center', padding: '60px' }}>
                        <Loader className="spin" size={32} color="#3b5bfd" style={{ margin: '0 auto', animation: 'spin 1s linear infinite' }} />
                        <p style={{ marginTop: '12px', color: '#64748b' }}>Analyzing database for matching candidates...</p>
                      </td>
                    </tr>
                  ) : filteredCandidates.length > 0 ? filteredCandidates.map((c, idx) => {
                    const isSelected = selectedIds.includes(c.id);
                    return (
                      <tr 
                        key={idx} 
                        style={{ 
                          borderTop: '1px solid #f1f5f9',
                          background: isSelected ? '#f0fdf9' : 'white',
                          transition: 'background 0.15s'
                        }}
                      >
                        {/* ── Row checkbox ── */}
                        <td style={{ padding: '8px 8px', textAlign: 'center', verticalAlign: 'middle' }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(c.id)}
                            style={{ accentColor: '#3b5bfd', width: 16, height: 16, cursor: 'pointer' }}
                          />
                        </td>
                        <td style={{ padding: '8px 10px', verticalAlign: 'middle', textAlign: 'left', overflow: 'hidden' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', overflow: 'hidden' }}>
                            <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`${c.firstName} ${c.lastName}`}>{c.firstName} {c.lastName}</span>
                            {c.score > 5 && (
                              <span style={{ 
                                fontSize: '9px', 
                                background: '#dcfce7', 
                                color: '#1d4ed8', 
                                padding: '1px 6px', 
                                borderRadius: '12px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                width: 'fit-content',
                                marginTop: '2px',
                                fontWeight: 700,
                                textTransform: 'uppercase'
                              }}>
                                <Sparkles size={8} fill="#1d4ed8" /> Relevant
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '8px 6px', textAlign: 'center', verticalAlign: 'middle' }}>
                          <span style={{ fontWeight: 700, color: '#3b5bfd', background: '#f0fdfa', padding: '4px 10px', borderRadius: '6px', fontSize: '13px' }}>
                            {c.score}
                          </span>
                        </td>
                        <td style={{ padding: '8px 10px', color: '#475569', fontSize: '12px', textTransform: 'capitalize', verticalAlign: 'middle', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.jobTitle || 'N/A'}>{c.jobTitle || 'N/A'}</td>
                        <td style={{ padding: '8px 10px', color: '#475569', fontSize: '12px', verticalAlign: 'middle', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.email || 'N/A'}>{c.email || 'N/A'}</td>
                        <td style={{ padding: '8px 10px', color: '#475569', fontSize: '12px', verticalAlign: 'middle', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.phone || 'N/A'}>{c.phone || 'N/A'}</td>
                        <td style={{ padding: '8px 10px', color: '#475569', fontSize: '12px', verticalAlign: 'middle', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.location || 'N/A'}>{c.location || 'N/A'}</td>
                        <td style={{ padding: '8px 8px', color: '#475569', fontSize: '12px', verticalAlign: 'middle', whiteSpace: 'nowrap', textAlign: 'left' }}>
                          <strong>{c.experience}</strong> <span style={{ color: '#94a3b8' }}>yrs</span>
                        </td>
                        <td style={{ padding: '8px 12px', verticalAlign: 'middle', textAlign: 'left' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                            {c.skills.slice(0, 2).map((skill, i) => (
                              <span key={i} style={{ fontSize: '10px', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', color: '#475569', fontWeight: 500 }}>
                                {skill}
                              </span>
                            ))}
                            {c.skills.length > 2 && (
                              <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500, marginLeft: '2px' }}>
                                +{c.skills.length - 2}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', verticalAlign: 'middle' }}>
                          <span style={{ 
                            fontSize: '11px', 
                            padding: '2px 8px', 
                            borderRadius: '4px', 
                            fontWeight: 600,
                            background: c.isBench ? '#fef3c7' : '#f1f5f9',
                            color: c.isBench ? '#92400e' : '#475569',
                            border: c.isBench ? '1px solid #fde68a' : '1px solid #e2e8f0'
                          }}>
                            {c.isBench ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', verticalAlign: 'middle' }}>
                          {(() => {
                            const cEmail = (c.email || '').toLowerCase();
                            const cName = (`${c.firstName} ${c.lastName}`).toLowerCase().trim();
                            const isSubmitted = (cEmail && applications.some(app => (app.email || '').toLowerCase() === cEmail)) ||
                                                (cName && applications.some(app => (app.name || '').toLowerCase().trim() === cName));

                            if (isSubmitted) {
                              return (
                                <span style={{ 
                                  fontSize: '11px', 
                                  background: '#fef3c7', 
                                  color: '#92400e', 
                                  padding: '4px 8px', 
                                  borderRadius: '4px',
                                  fontWeight: 600,
                                  whiteSpace: 'nowrap',
                                  display: 'inline-block'
                                }}>
                                  Already Submitted
                                </span>
                              );
                            }

                            const candidateKey = c.id || c.email || `${c.firstName} ${c.lastName}`.trim();
                            const isThisCandSubmitting = submittingCandidateId === candidateKey;

                            return (
                              <button 
                                className="si-btn-save" 
                                style={{ 
                                  background: '#3b5bfd', 
                                  padding: '8px 12px', 
                                  fontSize: '12px', 
                                  borderRadius: '6px', 
                                  display: 'inline-flex', 
                                  alignItems: 'center', 
                                  gap: '8px', 
                                  margin: '0 auto',
                                  height: '36px',
                                  border: 'none',
                                  color: 'white',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  transition: 'transform 0.2s, background 0.2s'
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#124d47'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = '#3b5bfd'; e.currentTarget.style.transform = 'translateY(0)'; }}
                                onClick={() => handleSubmitProfile(c)}
                                disabled={submittingCandidateId !== null}
                              >
                                <Send size={14} /> {isThisCandSubmitting ? 'Submitting...' : 'Submit Profile'}
                              </button>
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan="11" style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                        {(searchTerm || regionFilter !== 'all' || dateQuickSelect !== 'all' || matchFilter !== 'all')
                          ? 'No matching candidates found for the selected filters.'
                          : 'No candidates found for this role.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="si-modal-footer" style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', justifyContent: 'space-between', display: 'flex', alignItems: 'center' }}>
            {/* Selection summary on left */}
            <div style={{ fontSize: '13px', color: '#64748b' }}>
              {selectedIds.length > 0
                ? `${selectedIds.length} candidate${selectedIds.length > 1 ? 's' : ''} selected`
                : 'Check rows to select candidates for bulk email'}
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              {/* Bulk Email button in footer too */}
              {selectedIds.length > 0 && (
                <button
                  onClick={() => setShowShareModal(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 18px',
                    background: '#3b5bfd',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: '13px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#124d47'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#3b5bfd'; }}
                >
                  <Mail size={15} />
                  Send Bulk Email ({selectedIds.length})
                </button>
              )}
              <button 
                className="si-btn-cancel" 
                onClick={onClose} 
                disabled={submittingCandidateId !== null}
                style={{ 
                  padding: '10px 30px', 
                  borderRadius: '8px', 
                  background: '#f1f5f9', 
                  border: '1px solid #e2e8f0',
                  color: '#475569',
                  fontWeight: 600,
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#1e293b'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#475569'; }}
              >
                Close
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* ── Share Resume / Bulk Email Modal ─────────────────────────────────── */}
      <ShareResumeModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        candidates={selectedCandidatesForShare}
        autoPopulateCandidateEmails={true}
        roleName={role.role}
        roleId={role.id}
        onSuccess={() => {
          setSelectedIds([]);
          setShowShareModal(false);
        }}
      />
    </>
  );
};

export default SourceCandidateModal;
